import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import VisitorsIcon from 'assets/Icons/sidebarIcons/VisitorsIcon';
import { useQuery } from 'react-query';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import useSaveVisitor from './crud/useSave';
import { VisitorProps } from 'services/hooks/visitor-service';
import Modal from 'components/elements/Modal';
import RoleSelectionForm from './roleSelectionForm';
import PrimaryButton from 'components/elements/Form/button/primaryButton';

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: (newVisitor: VisitorProps) => Promise<VisitorProps>;
  updateQuery: (uuid: string | undefined, updatedVisitor: VisitorProps) => Promise<VisitorProps>;
  editQuery?: (uuid: string | undefined) => Promise<VisitorProps>;
  editUUID?: string;
  queryName: string;
};

/* The `phoneRegExp` constant is a regular expression pattern used to validate phone numbers. */
const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

/* The code block is defining a validation schema using the `yup` library for a form in a React
component. If these validation rules are not met, an error message will be displayed.*/
const CreateVisitorSchema = yup.object().shape({
  phoneNumber: yup
    .string()
    .required('Phone Number is required')
    .min(10, 'Phone Number needs to be of 10 digits')
    .max(10, 'Phone Number needs to be of 10 digits')
    .matches(phoneRegExp, 'Phone Number is not valid'),
  buildingUuid: yup.string().required('Building is required'),
  email: yup.string().when({
    is: (exists: string) => !!exists,
    then: yup.string().email().required('Email is required'),
    otherwise: yup.string().notRequired()
  })
});

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  const [getVisitorResult, setGetVisitorResult] = useState<VisitorProps | null>(null);
  const [disabled, setdisabled] = useState(false);

  const { isSuccess, data, refetch } = useQuery<VisitorProps | undefined, Error>(
    'visitor',
    () => (editUUID ? editQuery?.(editUUID) : undefined),
    {
      enabled: editUUID ? true : false,
      onError: (err: any) => {
        console.log(err.response?.data || err);
      }
    }
  );

  /* The `useForm` hook from the `react-hook-form` library is being used to create a form with form
validation. */
  const methods = useForm({
    defaultValues: getVisitorResult
      ? {
          uuid: getVisitorResult?.uuid,
          phoneNumber: getVisitorResult?.phoneNumber,
          email: getVisitorResult?.email,
          firstName: getVisitorResult?.firstName,
          lastName: getVisitorResult?.lastName,
          reason: getVisitorResult?.reason,
          tag: getVisitorResult?.tag,
          company: getVisitorResult?.company,
          buildingUuid: getVisitorResult?.buildingUuid
        }
      : {
          uuid: '',
          phoneNumber: '',
          email: '',
          firstName: '',
          lastName: '',
          reason: '',
          tag: '',
          company: '',
          buildingUuid: ''
        },
    resolver: yupResolver(CreateVisitorSchema)
  });

  useEffect(() => {
    if (data && editUUID) {
      refetch();
      setGetVisitorResult(data);
      /* `methods.reset(data)` is being used to set the initial values of the form fields to the values retrieved from the `data` object. The 
      `data` object contains the Visitor details fetched from the API when editing an existing visitor. By calling `methods.reset()` with the 
      desired initial values, the form fields will be populated with the fetched data when the form is rendered. */
      methods.reset(data);
    }
  }, [data, editUUID, methods, refetch]);

  /* The `saveVisitorMutation` variable is created using the `useSaveVisitor` hook. This hook is used to
  handle the mutation (create or update) of a visitor. It takes in several parameters: */
  const saveVisitorMutation = useSaveVisitor({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      setdisabled(false);
      if (setEditUUID) setEditUUID('');
    }
  });

  /* The function onSubmit is used to save visitor information and disable the submit button.
   * The parameter `e` is of type `VisitorProps`. It is used to represent the event object that is passed to the `onSubmit` function. */
  const onSubmit = async (e: VisitorProps) => {
    setdisabled(true);

    saveVisitorMutation.mutate({
      uuid: e?.uuid,
      phoneNumber: e?.phoneNumber,
      email: e?.email,
      firstName: e?.firstName,
      lastName: e?.lastName,
      reason: e?.reason,
      tag: e?.tag,
      company: e?.company,
      buildingUuid: e?.buildingUuid
    });
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            <RoleSelectionForm Controller={Controller} methods={methods} />

            <Input
              label="First Name"
              name="firstName"
              type="text"
              // data-testid="example"
              placeholder="Enter First Name"
              autoFocus
              methods={methods}
            />
            <Input
              label="Last Name"
              name="lastName"
              type="text"
              // data-testid="example"
              placeholder="Enter Last Name"
              autoFocus
              methods={methods}
            />
            <Input
              label="Phone Number"
              name="phoneNumber"
              type="tel"
              // data-testid="example"
              placeholder="Enter Phone Number"
              autoFocus
              methods={methods}
            />
            <Input
              label="Email"
              name="email"
              type="email"
              // data-testid="example"
              placeholder="Enter Email"
              autoFocus
              methods={methods}
            />
            <Input
              label="Reason"
              name="reason"
              type="text"
              // data-testid="example"
              placeholder="Enter Reason"
              autoFocus
              methods={methods}
            />
            <Input
              label="Tag"
              name="tag"
              type="text"
              // data-testid="example"
              placeholder="Enter Tag"
              autoFocus
              methods={methods}
            />
            <Input
              label="Company"
              name="company"
              type="text"
              // data-testid="example"
              placeholder="Enter Company"
              autoFocus
              methods={methods}
            />
          </div>
          <PrimaryButton
            buttonLabel={entityName}
            // onClick={onClick}
            iconLeft={<VisitorsIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            disabled={disabled}
          />
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
